import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { BrandMark } from "../components/Brand";
import TrustCues from "../components/TrustCues";
import { api, formatCurrency } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { getAttributionPayload } from "../lib/attribution";
import { ArrowLeft, ArrowRight, ShieldCheck, Sparkles, Lock, Upload, FileText, Trash2, Check, Eraser } from "lucide-react";
import { toast } from "sonner";
import { US_STATES, ACCIDENT_TYPES, INTAKE_STEPS, SERVED_STATE_CODES, stateName } from "../data/intake";
import { OhioExampleDisclosure } from "../components/OhioExampleDisclosure";

const STORAGE_KEY = "instabridge_intake_draft_v2";
const TOTAL_STEPS = INTAKE_STEPS.length;

const EMPTY = {
  requested_amount: 5000,
  name: "", phone: "", email: "",
  street_address: "", city: "", state: "", zip_code: "", claim_state: "",
  date_of_birth: "", ssn: "",
  has_prior_funding: false, prior_funding_date: "", prior_funding_company: "", prior_funding_amount: "",
  // Case routing
  case_type: "auto_accident",
  // Auto-accident fields (legacy — unchanged)
  accident_type: "", accident_date: "", accident_description: "", injury_description: "",
  insurance_company: "", policy_limits: "",
  // Workers' comp plaintiff intake (only used when case_type === 'workers_comp')
  employer_name: "", body_parts_injured: "",
  currently_receiving_benefits: "", has_attorney: null,
  attorney_name: "", attorney_firm: "", attorney_city: "", attorney_state: "",
  attorney_phone: "", attorney_email: "",
  acknowledgement_accepted: false, client_signature_name: "",
  documents: [],
};

const formatSSN = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const formatPhone = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export default function Apply() {
  useSEO("Apply for funding", "Premium pre-settlement funding application — fast, secure, mobile-friendly. Most decisions in 24 hours.", { canonical: "https://instabridgefunding.com/apply" });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [showResume, setShowResume] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);

  // Auto-save on every change after the first interaction
  const initialized = useRef(false);
  useEffect(() => {
    if (!initialized.current) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, step, savedAt: new Date().toISOString() }));
    } catch (err) { console.error("apply-draft-autosave", err); }
  }, [data, step]);

  // Check for saved draft on first load AND apply URL prefill from the homepage pre-qual widget
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.data?.name || parsed?.data?.email || parsed?.step > 1) {
          setSavedDraft(parsed);
          setShowResume(true);
        }
      }
    } catch (err) { console.error("apply-draft-load", err); }
    // Apply URL prefill (from PreQualWidget) — runs in parallel with draft prompt
    const sp = searchParams;
    const amt = Number(sp.get("amount"));
    const accident = sp.get("accident_type");
    const stateAbbr = sp.get("state");
    if (Number.isFinite(amt) && amt >= 500 && amt <= 25000) {
      setData((d) => ({ ...d, requested_amount: amt }));
    }
    if (accident) setData((d) => ({ ...d, accident_type: accident }));
    if (stateAbbr) setData((d) => ({ ...d, state: stateAbbr }));
    initialized.current = true;
  }, []);

  const resumeDraft = () => {
    setData({ ...EMPTY, ...savedDraft.data });
    setStep(savedDraft.step || 1);
    setShowResume(false);
    toast.success("Draft restored");
  };
  const discardDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShowResume(false);
    setSavedDraft(null);
  };

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v?.target ? v.target.value : v }));
  const outOfArea = step >= 2 && Boolean(data.state) && !SERVED_STATE_CODES.includes(data.state);

  const validate = () => {
    if (outOfArea) return false;
    if (step === 1) return Number(data.requested_amount) >= 500 && Number(data.requested_amount) <= 25000;
    if (step === 2) return data.name && data.phone && data.email.includes("@") && data.street_address && data.city && data.state && data.claim_state && data.zip_code && data.date_of_birth;
    if (step === 3) return !data.has_prior_funding || (data.prior_funding_company && data.prior_funding_amount);
    if (step === 4) {
      if (data.case_type === "workers_comp") {
        return (
          data.accident_date &&
          data.employer_name.trim().length > 1 &&
          data.accident_description.length > 5 &&
          data.body_parts_injured.trim().length > 1 &&
          data.currently_receiving_benefits &&
          data.has_attorney !== null
        );
      }
      return data.accident_type && data.accident_date && data.accident_description.length > 5 && data.injury_description.length > 5;
    }
    if (step === 5) {
      // WC claimants without an attorney: don't gate the step (we'll capture follow-up info downstream).
      if (data.case_type === "workers_comp" && data.has_attorney === false) return true;
      return data.attorney_name && data.attorney_email.includes("@") && data.attorney_phone && data.attorney_state;
    }
    if (step === 6) return data.acknowledgement_accepted && data.client_signature_name.trim().length > 1;
    return false;
  };

  const next = () => {
    if (outOfArea) { toast.error(`Instabridge does not currently offer funding in ${stateName(data.state)}`); return; }
    if (!validate()) { toast.error("Please complete all required fields on this step"); return; }
    if (step < TOTAL_STEPS) { setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };
  const back = () => { if (step > 1) { setStep(step - 1); window.scrollTo({ top: 0, behavior: "smooth" }); } };

  const submit = async () => {
    if (!validate()) { toast.error("Please complete the acknowledgement and signature"); return; }
    setSubmitting(true);
    try {
      // For WC, derive `injury_description` from body parts + how-it-happened so the
      // backend's required field is satisfied without forcing a redundant question.
      const isWC = data.case_type === "workers_comp";
      const injuryDescription = isWC
        ? `Workers' compensation injury. Body parts: ${data.body_parts_injured || "n/a"}. ${data.accident_description || ""}`.trim()
        : data.injury_description;
      const accidentType = isWC ? "Workers' Compensation" : data.accident_type;
      const payload = {
        ...data,
        accident_type: accidentType,
        injury_description: injuryDescription,
        requested_amount: Number(data.requested_amount),
        prior_funding_amount: data.has_prior_funding && data.prior_funding_amount ? Number(data.prior_funding_amount) : null,
        // First-touch attribution — landing page, referrer, utm params, first-visit timestamp.
        // Persisted in localStorage on first app boot; unchanged across the multi-step
        // application flow so consolidation to a single Application row is straightforward.
        ...getAttributionPayload(),
      };
      const { data: created } = await api.post("/applications", payload);
      // Upload any documents
      for (const docMeta of data.documents) {
        await api.post(`/applications/${created.id}/documents`, docMeta);
      }
      localStorage.removeItem(STORAGE_KEY);
      navigate(`/apply/success?id=${created.id}&attorney=${encodeURIComponent(data.attorney_name)}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.code === "out_of_service_area" ? detail.message : "Submission failed — please try again");
      setSubmitting(false);
    }
  };

  const progress = (step / TOTAL_STEPS) * 100;
  const current = INTAKE_STEPS[step - 1];

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="apply-page">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
        <div className="mx-auto max-w-5xl px-5 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <div className="hidden sm:flex items-center gap-2 text-xs text-[#6B7280]">
            <Lock className="w-3.5 h-3.5 text-[#0066FF]" /> Bank-level encryption · 256-bit TLS
          </div>
        </div>
        <Progress value={progress} className="h-1 bg-[#F3F4F6] rounded-none" data-testid="apply-progress" />
      </header>

      {/* Stepper */}
      <div className="border-b border-[#E5E7EB] bg-white">
        <div className="mx-auto max-w-5xl px-5 lg:px-10 py-4 overflow-x-auto">
          <ol className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm whitespace-nowrap">
            {INTAKE_STEPS.map((s, i) => {
              const isDone = step > s.id;
              const isCurrent = step === s.id;
              return (
                <li key={s.id} className="flex items-center gap-2 sm:gap-3">
                  <div className={`flex items-center gap-2 ${isCurrent ? "text-[#0A2540]" : isDone ? "text-[#0066FF]" : "text-[#9CA3AF]"}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold transition-all ${
                      isDone ? "bg-[#0066FF] text-white" : isCurrent ? "bg-[#0A2540] text-white scale-110" : "bg-[#F3F4F6]"
                    }`}>
                      {isDone ? <Check className="w-3.5 h-3.5" /> : s.id}
                    </div>
                    <span className={`font-medium ${isCurrent ? "" : "hidden sm:inline"}`}>{s.label}</span>
                  </div>
                  {i < INTAKE_STEPS.length - 1 && <span className="w-4 sm:w-8 h-px bg-[#E5E7EB]" />}
                </li>
              );
            })}
          </ol>
        </div>
      </div>

      {/* Resume banner */}
      {showResume && (
        <div className="bg-[#FFFBEB] border-b border-[#FDE68A]">
          <div className="mx-auto max-w-3xl px-5 lg:px-10 py-3 flex items-center justify-between gap-4 flex-wrap text-sm">
            <span className="text-[#92400E]">📝 We saved your draft from {new Date(savedDraft?.savedAt).toLocaleString()}. Resume where you left off?</span>
            <div className="flex gap-2">
              <button onClick={resumeDraft} className="text-[#0A2540] font-semibold hover:underline" data-testid="resume-draft-btn">Resume</button>
              <span className="text-[#92400E]">·</span>
              <button onClick={discardDraft} className="text-[#92400E] hover:underline" data-testid="discard-draft-btn">Start fresh</button>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-5 lg:px-10 pt-10 pb-20">
        <div className="mb-8 animate-fade-up">
          <div className="uppercase-label">Section {step} of {TOTAL_STEPS}</div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#0A2540] font-semibold mt-2">{current.title}</h1>
          <p className="text-[#6B7280] mt-2">{current.description}</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-6 sm:p-9 animate-slide-in-right" key={step}>
          {step === 1 && <StepFunding value={Number(data.requested_amount) || 0} onChange={set("requested_amount")} stateAbbr={data.state} />}
          {step === 2 && <StepPersonal data={data} set={set} />}
          {outOfArea && <OutOfAreaNotice state={data.state} />}
          {step === 3 && <StepPriorFunding data={data} set={set} />}
          {step === 4 && <StepCase data={data} set={set} />}
          {step === 5 && <StepAttorney data={data} set={set} />}
          {step === 6 && <StepReview data={data} set={set} goToStep={setStep} />}

          {/* Footer nav */}
          <div className="flex justify-between mt-10 pt-6 border-t border-[#E5E7EB]">
            <Button variant="ghost" onClick={back} disabled={step === 1} data-testid="btn-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            {step < TOTAL_STEPS ? (
              <Button onClick={next} disabled={outOfArea} className="btn-primary-navy rounded-full h-12 px-7" data-testid="btn-next">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting || outOfArea} className="btn-primary-navy rounded-full h-12 px-8" data-testid="btn-submit">
                {submitting ? "Submitting…" : "Submit application"}
              </Button>
            )}
          </div>

          {/* Subtle trust cue directly below the submit CTA on the final step */}
          {step === TOTAL_STEPS && (
            <TrustCues variant="trust-line" audience="consumer" align="center" className="!mt-5" testid="apply-submit-trust" />
          )}
        </div>

        {/* Trust strip below form */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4 text-xs text-[#6B7280]">
          <TrustItem icon={ShieldCheck} t="Bank-level security" d="256-bit TLS encryption end-to-end" />
          <TrustItem icon={Lock} t="Your data is private" d="We never share your information without consent" />
          <TrustItem icon={Sparkles} t="No credit check" d="Approval based on your case, not your credit" />
        </div>

        {/* Payout-method reassurance — funded fast via your preferred method */}
        <div className="mt-6 flex justify-center">
          <TrustCues variant="payout-strip" audience="consumer" testid="apply-payout-strip" />
        </div>
      </div>
    </div>
  );
}

const TrustItem = ({ icon: Icon, t, d }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
    <div>
      <div className="text-[#0A2540] font-medium">{t}</div>
      <div className="text-[#6B7280]">{d}</div>
    </div>
  </div>
);

const Field = ({ label, id, required, children, hint }) => (
  <div>
    <Label htmlFor={id} className="text-sm text-[#374151] font-medium">
      {label}{required && <span className="text-[#B91C1C] ml-0.5">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {hint && <div className="text-xs text-[#6B7280] mt-1">{hint}</div>}
  </div>
);

const OutOfAreaNotice = ({ state }) => (
  <div className="mt-6 p-5 rounded-xl bg-[#FFFBEB] border border-[#FDE68A] text-sm text-[#92400E] leading-relaxed" data-testid="apply-out-of-area-notice">
    <div className="font-semibold text-[#0A2540]">Funding is not available in {stateName(state)}</div>
    <p className="mt-1">Instabridge does not currently offer funding to residents of {stateName(state)}. We're continually working to expand our service areas. Contact us to learn about future availability, or see the states we currently serve.</p>
    <div className="mt-3 flex gap-3 flex-wrap">
      <Link to="/contact"><Button className="btn-primary-navy rounded-full h-10 px-5" data-testid="apply-out-of-area-contact-btn">Contact us</Button></Link>
      <Link to="/states" className="self-center text-[#0066FF] font-medium hover:underline" data-testid="apply-out-of-area-states-link">States we serve</Link>
    </div>
  </div>
);

// ============ Step 1: Funding Amount ============
const PRESETS = [1000, 2500, 5000, 10000, 15000, 25000];
function StepFunding({ value, onChange, stateAbbr }) {
  const est6mo = Math.round(value * Math.pow(1.038, 6));
  const showEstimate = !stateAbbr || SERVED_STATE_CODES.includes(stateAbbr);
  return (
    <div className="space-y-7" data-testid="step-funding">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#0066FF] text-white rounded-2xl p-7 lg:p-9 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
        <div className="relative">
          <div className="uppercase-label !text-white/60 mb-2 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Your advance</div>
          <div className="font-display text-6xl lg:text-7xl font-semibold tracking-tight tabular-nums leading-none" data-testid="amount-display">
            {formatCurrency(value)}
          </div>
          {showEstimate && (
            <>
              <div className="mt-5 pt-5 border-t border-white/10 flex justify-between items-center text-sm" data-testid="apply-ohio-estimate">
                <span className="text-white/70">{stateAbbr === "OH" ? "Illustrative Ohio Funding Estimate" : "Illustrative Ohio Funding Example"}: payoff if settled in 6 months</span>
                <span className="font-display text-lg font-semibold tabular-nums">{formatCurrency(est6mo)}</span>
              </div>
              <OhioExampleDisclosure stateAbbr={stateAbbr} className="mt-3 text-xs text-white/60 leading-relaxed" testid="apply-ohio-disclosure" />
            </>
          )}
        </div>
      </div>
      <div className="px-1">
        <Slider min={500} max={25000} step={100} value={[value]} onValueChange={(v) => onChange(v[0])} data-testid="amount-slider" />
        <div className="flex justify-between mt-2 text-[11px] text-[#9CA3AF] font-medium tabular-nums">
          <span>$500</span><span>$5K</span><span>$10K</span><span>$15K</span><span>$20K</span><span>$25K</span>
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-2.5">Or pick a common amount</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p} type="button" onClick={() => onChange(p)}
              className={`px-4 h-10 rounded-full text-sm font-medium border transition-all ${
                value === p ? "bg-[#0A2540] text-white border-[#0A2540] shadow-sm" : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#0066FF] hover:text-[#0066FF]"
              }`} data-testid={`amount-preset-${p}`}>${p.toLocaleString()}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ Step 2: Personal ============
function StepPersonal({ data, set }) {
  return (
    <div className="space-y-5" data-testid="step-personal">
      <Field label="Full legal name" id="name" required>
        <Input id="name" value={data.name} onChange={set("name")} placeholder="Jordan A. Mitchell" data-testid="input-name" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Mobile phone" id="phone" required>
          <Input id="phone" value={data.phone} onChange={(e) => set("phone")(formatPhone(e.target.value))} placeholder="(555) 123-4567" data-testid="input-phone" />
        </Field>
        <Field label="Email address" id="email" required>
          <Input id="email" type="email" value={data.email} onChange={set("email")} placeholder="you@example.com" data-testid="input-email" />
        </Field>
      </div>
      <Field label="Street address" id="street_address" required>
        <Input id="street_address" value={data.street_address} onChange={set("street_address")} placeholder="123 Main St, Apt 4B" data-testid="input-street" />
      </Field>
      <div className="grid sm:grid-cols-3 gap-5">
        <Field label="City" id="city" required>
          <Input id="city" value={data.city} onChange={set("city")} placeholder="Cleveland" data-testid="input-city" />
        </Field>
        <Field label="State where you live" id="state" required>
          <Select value={data.state} onValueChange={set("state")}>
            <SelectTrigger id="state" data-testid="input-state"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="max-h-72">{US_STATES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="ZIP" id="zip_code" required>
          <Input id="zip_code" value={data.zip_code} onChange={(e) => set("zip_code")(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="44101" data-testid="input-zip" inputMode="numeric" />
        </Field>
      </div>
      <Field label="State where your legal claim is filed" id="claim_state" required hint="If your case hasn't been filed yet, choose the state where it will be filed, or select “I'm not sure.”">
        <Select value={data.claim_state} onValueChange={set("claim_state")}>
          <SelectTrigger id="claim_state" data-testid="input-claim-state"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="UNSURE" data-testid="claim-state-unsure">I'm not sure</SelectItem>
            {US_STATES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Date of birth" id="dob" required>
          <Input id="dob" type="date" value={data.date_of_birth} onChange={set("date_of_birth")} data-testid="input-dob" />
        </Field>
        <Field label="Social Security Number" id="ssn" hint="Encrypted in transit and at rest. Used for identity verification only.">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#0066FF]" />
            <Input id="ssn" value={data.ssn} onChange={(e) => set("ssn")(formatSSN(e.target.value))} placeholder="123-45-6789" className="pl-9 font-mono" data-testid="input-ssn" inputMode="numeric" />
          </div>
        </Field>
      </div>
      <SecurityNote>Your information is encrypted and protected. We never sell or share personal data without your written consent.</SecurityNote>
    </div>
  );
}

// ============ Step 3: Prior Funding ============
function StepPriorFunding({ data, set }) {
  return (
    <div className="space-y-6" data-testid="step-prior-funding">
      <div className="flex items-center justify-between p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
        <div>
          <div className="font-display font-semibold text-[#0A2540]">Have you received prior funding for this case?</div>
          <div className="text-sm text-[#6B7280] mt-1">From another funding company, on the same legal claim.</div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => set("has_prior_funding")(false)}
            className={`px-5 h-10 rounded-full text-sm font-medium border transition-all ${
              !data.has_prior_funding ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#0066FF]"
            }`} data-testid="prior-funding-no">No</button>
          <button type="button" onClick={() => set("has_prior_funding")(true)}
            className={`px-5 h-10 rounded-full text-sm font-medium border transition-all ${
              data.has_prior_funding ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#0066FF]"
            }`} data-testid="prior-funding-yes">Yes</button>
        </div>
      </div>
      {data.has_prior_funding && (
        <div className="space-y-5 animate-fade-up">
          <Field label="Prior funding company" id="prior_funding_company" required>
            <Input id="prior_funding_company" value={data.prior_funding_company} onChange={set("prior_funding_company")} placeholder="e.g., Oasis Financial" data-testid="input-prior-company" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Date of prior funding" id="prior_funding_date">
              <Input id="prior_funding_date" type="date" value={data.prior_funding_date} onChange={set("prior_funding_date")} data-testid="input-prior-date" />
            </Field>
            <Field label="Amount received" id="prior_funding_amount" required>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">$</span>
                <Input id="prior_funding_amount" type="number" min="0" value={data.prior_funding_amount} onChange={set("prior_funding_amount")} className="pl-7" placeholder="5000" data-testid="input-prior-amount" />
              </div>
            </Field>
          </div>
        </div>
      )}
      {!data.has_prior_funding && (
        <div className="text-sm text-[#6B7280] italic text-center py-4">No prior funding to report. You'll skip ahead to your case details.</div>
      )}
    </div>
  );
}

// ============ Step 4: Case ============
function StepCase({ data, set }) {
  const addDoc = (file) => {
    const meta = {
      id: Math.random().toString(36).slice(2, 11),
      name: file.name,
      category: "other",
      size_kb: Math.round(file.size / 1024),
      uploaded_by: "claimant",
    };
    set("documents")([...(data.documents || []), meta]);
  };
  const removeDoc = (id) => set("documents")((data.documents || []).filter((d) => d.id !== id));
  const onFiles = (e) => Array.from(e.target.files || []).slice(0, 5).forEach(addDoc);

  const isWC = data.case_type === "workers_comp";

  return (
    <div className="space-y-5" data-testid="step-case">
      {/* Case-type selector (always first) — drives the rest of this step */}
      <Field label="Case type" id="case_type" required hint="We tailor the rest of the questions based on your case.">
        <Select value={data.case_type || "auto_accident"} onValueChange={set("case_type")}>
          <SelectTrigger id="case_type" data-testid="input-case-type"><SelectValue placeholder="Select case type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto_accident">Auto Accident</SelectItem>
            <SelectItem value="workers_comp">Workers&apos; Compensation</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {!isWC ? (
        <>
          <Field label="Type of accident" id="accident_type" required>
            <Select value={data.accident_type} onValueChange={set("accident_type")}>
              <SelectTrigger id="accident_type" data-testid="input-accident-type"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{ACCIDENT_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Date of accident / incident" id="accident_date" required>
            <Input id="accident_date" type="date" value={data.accident_date} onChange={set("accident_date")} data-testid="input-accident-date" />
          </Field>
          <Field label="What happened?" id="accident_description" required hint="A short, plain-language description of the accident or incident.">
            <Textarea id="accident_description" rows={3} value={data.accident_description} onChange={set("accident_description")} placeholder="e.g., I was rear-ended at a red light on I-71 by a delivery truck…" data-testid="input-accident-description" />
          </Field>
          <Field label="Description of your injuries" id="injury_description" required hint="Current symptoms, treatments, and any planned procedures.">
            <Textarea id="injury_description" rows={4} value={data.injury_description} onChange={set("injury_description")} placeholder="e.g., Whiplash, lower-back pain, ongoing physical therapy 2x/week. MRI scheduled next month." data-testid="input-injury-description" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Insurance company (defendant's)" id="insurance_company">
              <Input id="insurance_company" value={data.insurance_company} onChange={set("insurance_company")} placeholder="e.g., State Farm, GEICO" data-testid="input-insurance" />
            </Field>
            <Field label="Policy limits" id="policy_limits" hint="OK if not yet known.">
              <Input id="policy_limits" value={data.policy_limits} onChange={set("policy_limits")} placeholder="e.g., $100,000 / $300,000" data-testid="input-policy-limits" />
            </Field>
          </div>
        </>
      ) : (
        <>
          <Field label="Date of injury" id="accident_date" required>
            <Input id="accident_date" type="date" value={data.accident_date} onChange={set("accident_date")} data-testid="input-wc-injury-date" />
          </Field>
          <Field label="Employer name" id="employer_name" required>
            <Input id="employer_name" value={data.employer_name} onChange={set("employer_name")} placeholder="e.g., Acme Logistics, Inc." data-testid="input-wc-employer" />
          </Field>
          <Field label="Brief description of how the injury occurred" id="accident_description" required hint="A short summary — what were you doing and what happened.">
            <Textarea id="accident_description" rows={3} value={data.accident_description} onChange={set("accident_description")} placeholder="e.g., I slipped on a wet warehouse floor while unloading a pallet and fell on my back." data-testid="input-wc-how" />
          </Field>
          <Field label="What body parts were injured?" id="body_parts_injured" required hint="Lower back, right shoulder, left knee, etc.">
            <Input id="body_parts_injured" value={data.body_parts_injured} onChange={set("body_parts_injured")} placeholder="e.g., Lower back and right shoulder" data-testid="input-wc-body-parts" />
          </Field>
          <Field label="Are you currently receiving workers' compensation benefits?" id="currently_receiving_benefits" required>
            <Select value={data.currently_receiving_benefits} onValueChange={set("currently_receiving_benefits")}>
              <SelectTrigger id="currently_receiving_benefits" data-testid="input-wc-benefits"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="unsure">Unsure</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Do you have an attorney representing you?" id="has_attorney" required>
            <Select
              value={data.has_attorney === true ? "yes" : data.has_attorney === false ? "no" : ""}
              onValueChange={(v) => set("has_attorney")(v === "yes")}
            >
              <SelectTrigger id="has_attorney" data-testid="input-wc-has-attorney"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </>
      )}

      {/* Document upload (shared) */}
      <div>
        <Label className="text-sm text-[#374151] font-medium">Optional: Upload supporting documents</Label>
        <div className="mt-1.5 border-2 border-dashed border-[#E5E7EB] hover:border-[#0066FF] transition-colors rounded-xl p-6 text-center bg-[#F9FAFB]">
          <Upload className="w-8 h-8 text-[#0066FF] mx-auto mb-2" />
          <div className="text-sm text-[#0A2540] font-medium">Click to upload or drag files here</div>
          <div className="text-xs text-[#6B7280] mt-1">{isWC ? "Incident report, medical records, benefit notices — up to 5 files" : "Police report, medical records, photos — up to 5 files"}</div>
          <input type="file" multiple accept="image/*,application/pdf" onChange={onFiles}
            className="absolute inset-0 opacity-0 cursor-pointer" style={{ display: "none" }} id="doc-upload" data-testid="doc-upload-input" />
          <label htmlFor="doc-upload" className="inline-block mt-3 px-4 h-9 leading-9 bg-white border border-[#E5E7EB] rounded-full text-sm font-medium text-[#0A2540] hover:border-[#0066FF] cursor-pointer">
            Browse files
          </label>
        </div>
        {(data.documents || []).length > 0 && (
          <div className="mt-3 space-y-2">
            {data.documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-3 border border-[#E5E7EB] rounded-lg bg-white">
                <div className="flex items-center gap-2 text-sm min-w-0">
                  <FileText className="w-4 h-4 text-[#0066FF] flex-shrink-0" />
                  <span className="truncate text-[#0A2540]">{d.name}</span>
                  <span className="text-xs text-[#6B7280]">({(d.size_kb / 1024).toFixed(2)} MB)</span>
                </div>
                <button type="button" onClick={() => removeDoc(d.id)} className="text-[#B91C1C] hover:bg-[#FEF2F2] p-1.5 rounded" data-testid={`remove-doc-${d.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Step 5: Attorney ============
function StepAttorney({ data, set }) {
  return (
    <div className="space-y-5" data-testid="step-attorney">
      <Field label="Attorney's full name" id="attorney_name" required>
        <Input id="attorney_name" value={data.attorney_name} onChange={set("attorney_name")} placeholder="Sandra Whitfield" data-testid="input-attorney-name" />
      </Field>
      <Field label="Law firm name" id="attorney_firm">
        <Input id="attorney_firm" value={data.attorney_firm} onChange={set("attorney_firm")} placeholder="Whitfield Law Group" data-testid="input-attorney-firm" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Firm city" id="attorney_city">
          <Input id="attorney_city" value={data.attorney_city} onChange={set("attorney_city")} placeholder="Cleveland" data-testid="input-attorney-city" />
        </Field>
        <Field label="Firm state" id="attorney_state" required>
          <Select value={data.attorney_state} onValueChange={set("attorney_state")}>
            <SelectTrigger id="attorney_state" data-testid="input-attorney-state"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="max-h-72">{US_STATES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Attorney phone" id="attorney_phone" required>
          <Input id="attorney_phone" value={data.attorney_phone} onChange={(e) => set("attorney_phone")(formatPhone(e.target.value))} placeholder="(555) 987-6543" data-testid="input-attorney-phone" />
        </Field>
        <Field label="Attorney email" id="attorney_email" required>
          <Input id="attorney_email" type="email" value={data.attorney_email} onChange={set("attorney_email")} placeholder="legal@firm.com" data-testid="input-attorney-email" />
        </Field>
      </div>
      <SecurityNote>We'll send your attorney a secure verification link. They don't need to create an account — most verify in under 5 minutes.</SecurityNote>
    </div>
  );
}

// ============ Step 6: Review & Sign ============
function StepReview({ data, set, goToStep }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#0A2540";
  }, []);

  const getPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); const { x, y } = getPos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); setDrawing(true); };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const { x, y } = getPos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); setHasInk(true); persistSignature(); };
  const end = () => { setDrawing(false); persistSignature(); };
  const persistSignature = () => { try { set("client_signature_data")(canvasRef.current.toDataURL("image/png")); } catch (err) { console.error("persist-signature", err); } };
  const clear = () => { const c = canvasRef.current; const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height); setHasInk(false); set("client_signature_data")(""); };

  const sections = useMemo(() => [
    { step: 1, label: "Funding requested", value: formatCurrency(data.requested_amount) },
    { step: 2, label: "Claimant", value: `${data.name} · ${data.email} · ${data.phone}` },
    { step: 2, label: "Address", value: `${data.street_address}, ${data.city}, ${data.state} ${data.zip_code}` },
    { step: 2, label: "Claim filed in", value: data.claim_state === "UNSURE" ? "Not sure" : stateName(data.claim_state) },
    { step: 3, label: "Prior funding", value: data.has_prior_funding ? `Yes — ${data.prior_funding_company} ($${data.prior_funding_amount})` : "None" },
    { step: 4, label: "Case", value: data.case_type === "workers_comp"
        ? `Workers' Compensation — injured ${data.accident_date} at ${data.employer_name}`
        : `${data.accident_type} on ${data.accident_date}` },
    { step: 5, label: "Attorney", value: `${data.attorney_name}${data.attorney_firm ? ` · ${data.attorney_firm}` : ""} · ${data.attorney_email}` },
  ], [data]);

  return (
    <div className="space-y-7" data-testid="step-review">
      <div>
        <h3 className="font-display text-lg font-semibold text-[#0A2540]">Review your information</h3>
        <p className="text-sm text-[#6B7280] mt-1">Tap any row to make corrections.</p>
        <div className="mt-4 divide-y divide-[#F3F4F6] border border-[#E5E7EB] rounded-xl overflow-hidden">
          {sections.map((s, i) => (
            <button key={i} type="button" onClick={() => goToStep(s.step)} className="w-full flex justify-between items-start gap-4 p-4 text-left hover:bg-[#F9FAFB] transition-colors" data-testid={`review-row-${s.step}`}>
              <div className="min-w-0">
                <div className="uppercase-label">{s.label}</div>
                <div className="text-sm text-[#0A2540] mt-0.5 truncate">{s.value}</div>
              </div>
              <span className="text-xs text-[#0066FF] font-medium flex-shrink-0">Edit →</span>
            </button>
          ))}
        </div>
      </div>

      {/* Acknowledgement */}
      <div className="p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
        <div className="flex items-start gap-3">
          <Checkbox id="ack" checked={data.acknowledgement_accepted}
            onCheckedChange={(v) => set("acknowledgement_accepted")(Boolean(v))}
            className="mt-1" data-testid="ack-checkbox" />
          <Label htmlFor="ack" className="text-sm text-[#374151] leading-relaxed cursor-pointer">
            I certify that the information provided is accurate to the best of my knowledge. I authorize Instabridge Funding to contact my attorney to verify my case and discuss my application. I understand this is a non-recourse advance — if I do not recover from my legal claim, I owe nothing. I have read and accept the <a href="#" className="text-[#0066FF] underline">Privacy Policy</a> and <a href="#" className="text-[#0066FF] underline">Terms of Service</a>.
          </Label>
        </div>
      </div>

      {/* Signature pad */}
      <div>
        <Label className="text-sm text-[#374151] font-medium">Sign your application</Label>
        <p className="text-xs text-[#6B7280] mt-1">Type your full legal name and sign below to authorize.</p>
        <Input className="mt-3" placeholder="Full legal name" value={data.client_signature_name} onChange={set("client_signature_name")} data-testid="signature-name-input" />
        <div className="mt-3 rounded-xl border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB] overflow-hidden">
          <canvas ref={canvasRef}
            className="block w-full h-40 touch-none cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            data-testid="signature-canvas" />
        </div>
        <button type="button" onClick={clear} className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#0A2540]" data-testid="signature-clear-btn">
          <Eraser className="w-3 h-3" /> Clear signature
        </button>
        {hasInk && <div className="mt-2 text-xs text-[#047857] flex items-center gap-1"><Check className="w-3 h-3" /> Signature captured</div>}
      </div>

      <SecurityNote>By submitting, you acknowledge that all information is encrypted and stored securely. We will reach out within 1 business day with next steps.</SecurityNote>
    </div>
  );
}

const SecurityNote = ({ children }) => (
  <div className="flex items-start gap-2.5 p-4 bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg text-xs text-[#075985] leading-relaxed">
    <Lock className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#0066FF]" />
    <div>{children}</div>
  </div>
);
